/**
 * GridLibrary — Desk+ multi-exchange grid strategy templates.
 *
 * Templates deploy as *your* agent/strategy configs (WORK-PLAN C1/C3).
 * Not a signal marketplace, not copy trading, no fake returns.
 *
 * Deploy CTA: DeployAsAgentButton (Desk+ → agent desk with template query).
 * Craft floor: ivory cards / champagne accents; --color-* tokens only.
 */

'use client'

import React from 'react'
import Link from 'next/link'
import { usePlanEntitlements } from '@/hooks/use-plan-entitlements'
import { DeployAsAgentButton } from './DeployAsAgentButton'
import {
  GRID_TEMPLATES,
  type GridTemplate,
} from './grid-templates'
import styles from './GridLibrary.module.css'

export interface GridLibraryProps {
  /** Override catalog (defaults to honest GRID_TEMPLATES stubs). */
  templates?: readonly GridTemplate[]
  /**
   * Optional hook when Pro navigation to the agent desk is about to run.
   * Does not imply a live agent was created or any returns.
   */
  onNavigate?: (href: string) => void
  /** Optional class on the root. */
  className?: string
  /** Hide the short intro lead (when parent DeskShell already explains). */
  hideIntro?: boolean
  /**
   * Deploy control mode:
   * - navigate (default): /ai-trading?template=…&kind=grid&intent=deploy
   * - panel: honest configure stub then continue to agent desk
   */
  deployMode?: 'navigate' | 'panel'
}

export function GridLibrary({
  templates = GRID_TEMPLATES,
  onNavigate,
  className = '',
  hideIntro = false,
  deployMode = 'navigate',
}: GridLibraryProps) {
  const { canUseGridDca, isLoading: entitlementsLoading, isFree, tier } =
    usePlanEntitlements()

  return (
    <div
      className={`${styles.root} ${className}`.trim()}
      data-surface="grid-library"
      data-entitled={canUseGridDca ? 'true' : 'false'}
    >
      {!hideIntro ? (
        <p className={styles.intro}>
          Grid templates are deployable strategy configs for agents you supervise.
          No copy trading, no stranger signals, no advertised returns — only structure
          you configure (band, levels, exchange connectors).
        </p>
      ) : null}

      {!entitlementsLoading && !canUseGridDca ? (
        <p className={styles.planNote} role="status">
          Multi-exchange grid library is on Desk ($99.99/mo) and above.
          {isFree || tier === 'starter' || tier === 'none' ? (
            <>
              {' '}
              <Link className={styles.planLink} href="/settings/billing">
                Start free
              </Link>
            </>
          ) : null}
        </p>
      ) : null}

      <ul className={styles.grid} aria-label="Grid strategy templates">
        {templates.map((template) => (
          <li key={template.id} className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.kindChip}>Grid · agent config</span>
              <h3 className={styles.name}>{template.name}</h3>
            </div>

            <p className={styles.description}>{template.description}</p>

            <div className={styles.meta}>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Exchanges</span>
                <ul className={styles.exchangeList} aria-label="Exchange placeholders">
                  {template.exchanges.map((ex) => (
                    <li key={ex} className={styles.exchangeChip}>
                      {ex}
                    </li>
                  ))}
                </ul>
              </div>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Levels</span>
                <span className={styles.metaValue}>{template.defaults.gridLevels}</span>
              </div>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Range</span>
                <span className={styles.metaValue}>{template.defaults.rangeStyle}</span>
              </div>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Rebalance</span>
                <span className={styles.metaValue}>{template.defaults.rebalance}</span>
              </div>
            </div>

            {template.notes ? (
              <p className={styles.notes}>{template.notes}</p>
            ) : null}

            <div className={styles.footer}>
              <DeployAsAgentButton
                templateId={template.id}
                templateName={template.name}
                kind="grid"
                mode={deployMode}
                compact
                label="Deploy as agent"
                onNavigate={onNavigate}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default GridLibrary
